import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, User, Tag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({
    full_name: '',
    email: '',
    mobile: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching blogs from:', `${BACKEND_URL}/api/blog/posts`);
      const response = await axios.get(`${BACKEND_URL}/api/blog/posts`);
      console.log('Blog API response:', response.data);
      const posts = response.data.posts || [];
      setBlogs(posts);
      if (posts.length === 0) {
        console.log('No published blog posts found');
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setError('Failed to load blog posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnquiryChange = (e) => {
    setEnquiryForm({
      ...enquiryForm,
      [e.target.name]: e.target.value
    });
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, enquiryForm);
      toast.success('Enquiry submitted successfully!');
      setEnquiryForm({
        full_name: '',
        email: '',
        mobile: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Blog Header */}
      <section className="bg-[#0B2C4D] text-white py-16">
        <div className="container mx-auto px-6">
          <h1 className="text-5xl font-bold text-center mb-4">Our Blog</h1>
          <p className="text-xl text-center text-gray-200">
            Insights, updates and news from GoldenPort International
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Blog Posts */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#C9A24D] border-r-transparent mb-4"></div>
                  <p className="text-gray-600">Loading blog posts...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-4">{error}</p>
                  <Button onClick={fetchBlogs} className="bg-[#0B2C4D] hover:bg-[#0a2440] text-white">
                    Try Again
                  </Button>
                </div>
              ) : blogs.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-600 mb-2">No blog posts published yet</p>
                  <p className="text-gray-500 text-sm">Check back soon for updates!</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {blogs.map((blog) => (
                    <div
                      key={blog.blog_id}
                      className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-t-4 border-[#C9A24D] cursor-pointer"
                      onClick={() => navigate(`/blog/${blog.blog_id}`)}
                    >
                      {blog.image_url && (
                        <div 
                          className="h-64 bg-cover bg-center"
                          style={{ backgroundImage: `url('${blog.image_url}')` }}
                        />
                      )}
                      <div className="p-6">
                        <h2 className="text-3xl font-bold text-[#0B2C4D] mb-3 hover:text-[#C9A24D] transition-colors">
                          {blog.title}
                        </h2>
                        {blog.subtitle && (
                          <p className="text-xl text-gray-600 mb-4">{blog.subtitle}</p>
                        )}
                        <p className="text-gray-700 mb-4 line-clamp-3">
                          {blog.content.replace(/<[^>]*>/g, '').substring(0, 200)}...
                        </p>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            <span>{blog.author}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>

                        {blog.hashtags && blog.hashtags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {blog.hashtags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 bg-gray-100 text-[#0B2C4D] px-3 py-1 rounded-full text-sm"
                              >
                                <Tag className="w-3 h-3" />
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <Button className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D]">
                          Read More <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar - Quick Enquiry Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-gradient-to-br from-[#0B2C4D] to-[#1a3a5a] rounded-lg shadow-xl p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2 text-[#C9A24D]">Quick Enquiry</h3>
                  <p className="text-sm mb-6 text-gray-200">Get in touch with us for export solutions</p>
                  
                  <form onSubmit={handleEnquirySubmit} className="space-y-4">
                    <div>
                      <Input
                        name="full_name"
                        value={enquiryForm.full_name}
                        onChange={handleEnquiryChange}
                        required
                        placeholder="Your Name"
                        className="bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <Input
                        type="email"
                        name="email"
                        value={enquiryForm.email}
                        onChange={handleEnquiryChange}
                        required
                        placeholder="Your Email"
                        className="bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <Input
                        name="mobile"
                        value={enquiryForm.mobile}
                        onChange={handleEnquiryChange}
                        required
                        placeholder="Your Mobile"
                        className="bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <Textarea
                        name="message"
                        value={enquiryForm.message}
                        onChange={handleEnquiryChange}
                        required
                        placeholder="Your Message"
                        rows={4}
                        className="bg-white text-gray-900"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="w-full bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] font-semibold"
                    >
                      {submitting ? 'Submitting...' : 'Submit Enquiry'}
                    </Button>
                  </form>
                </div>

                {/* Contact CTA */}
                <div className="mt-6 bg-gray-50 rounded-lg p-6 text-center border-2 border-[#C9A24D]">
                  <h4 className="text-xl font-bold text-[#0B2C4D] mb-3">Need Help?</h4>
                  <p className="text-gray-700 mb-4">Contact us for expert export guidance</p>
                  <Button
                    className="bg-[#0B2C4D] hover:bg-[#0a2440] text-white"
                    onClick={() => navigate('/contact')}
                  >
                    Contact Us
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
